/**
 * useRefinePrompt
 * Magic Prompt Enhancer hook for Talk with Martha.
 *
 * Calls POST /api/refine to transform a vague query into a well-structured
 * request optimised for Martha's multi-agent orchestrator.
 *
 * Returns the refined text, confidence score, and change descriptions.
 */

import { useState, useCallback, useRef } from 'react';

export interface RefineResult {
  original: string;
  rewritten: string;
  confidence: number;
  changes_made: string[];
  latency_ms: number;
  model: string;
}

export interface UseRefinePromptResult {
  /** Whether a refinement request is in flight */
  isRefining: boolean;
  /** The latest refinement result (null until first refinement completes) */
  result: RefineResult | null;
  /** Refine the given query with optional conversation history */
  refine: (query: string, conversationHistory?: Array<{ role: string; content: string }>) => void;
  /** Clear the refinement result */
  clearResult: () => void;
  /** Error message if the request failed */
  error: string | null;
}

export function useRefinePrompt(): UseRefinePromptResult {
  const [isRefining, setIsRefining] = useState(false);
  const [result, setResult] = useState<RefineResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refine = useCallback(
    async (query: string, conversationHistory?: Array<{ role: string; content: string }>) => {
      if (!query.trim() || isRefining) return;

      // Abort any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setIsRefining(true);
      setError(null);

      try {
        const res = await fetch('/api/refine', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query,
            conversation_history: conversationHistory,
          }),
          signal: controller.signal,
          credentials: 'include',
        });

        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`Refine failed: ${res.status} ${body}`);
        }

        const data: RefineResult = await res.json();
        setResult(data);
      } catch (err: unknown) {
        if ((err as Error).name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : 'Refinement failed';
        setError(msg);
      } finally {
        setIsRefining(false);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [isRefining],
  );

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { isRefining, result, refine, clearResult, error };
}
